import React, { useImperativeHandle, useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  MenuProps,
  styled,
} from '@mui/material';

import {
  DaysOffMenuIcon,
  HolidaysMenuIcon,
  PlusIcon,
  WorkingTimeMenuIcon,
} from '../../../../../Icons';
import { SubmitButton } from '../../../../Button/SubmitButton';

import { Popup } from './components';

export enum TypeOfRuleType {
  WorkingTime,
  Holiday,
  DaysOff,
}

export const typeOfRuleArr = [
  {
    type: TypeOfRuleType.WorkingTime,
    label: 'Working time',
    id: '0',
    Icon: WorkingTimeMenuIcon,
  },
  {
    type: TypeOfRuleType.Holiday,
    label: 'Holiday',
    id: '1',
    Icon: HolidaysMenuIcon,
  },
  {
    type: TypeOfRuleType.DaysOff,
    label: 'Days off',
    id: '2',
    Icon: DaysOffMenuIcon,
  },
];

export enum TypeOfHolidayType {
  FreeDay,
  Shortened,
}

export const typeOfHolidayArr = [
  {
    label: 'Free day',
    id: '0',
  },
  {
    label: 'Shortened day',
    id: '1',
  },
];

const ActionButton = styled(SubmitButton)({
  '&[aria-expanded="true"]': {
    backgroundColor: '#FFF8FA',
    color: '#BF295C',
  },
});

export enum ScheduleMenuActionType {
  WorkTime,
  Holidays,
  DaysOff,
}

const ActionMenu = styled((props: MenuProps) => (
  <Menu elevation={0} {...props} />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: 8,
    minWidth: 222,
    color: '#363636',
    boxShadow: 'rgba(41, 66, 90, 0.12) 0px 4px 16px 0px',
    '& .MuiMenu-list': {
      padding: '0',
    },
    '& .MuiMenuItem-root': {
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '400',
      minHeight: '38px',

      '& .MuiSvgIcon-root': {
        fontSize: 16,
        marginRight: 12,
      },
      '&:active, &:hover': {
        backgroundColor: '#BF295C',
        color: 'white',
      },
    },
  },
}));

export const CreateScheduleMenu = React.forwardRef(
  (
    {
      periodId,
      onChangePeriodId,
      workingTimes,
      onWorkingTimesChange,
      daysOff,
      onDaysOffChange,
      holidays,
      onHolidaysChange,
      period,
    }: any,
    ref
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [typeOfRuleId, setTypeOfRuleId] = useState(
      typeOfRuleArr[TypeOfRuleType.WorkingTime]?.id
    );

    const [isEdit, setIsEdit] = useState(false);

    const [workingTimesIndex, setWorkingTimesIndex] = useState(0);
    const [daysOffReason, setDaysOffReason] = useState('');
    const [typeOfHolidayId, setTypeOfHolidayId] = useState(
      typeOfHolidayArr[TypeOfHolidayType.FreeDay]?.id
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleSubmit = (params: any) => {
      const typeOfRule = typeOfRuleArr.find(({ id }) => id === typeOfRuleId);
      if (typeOfRule?.type === TypeOfRuleType.WorkingTime) {
        // @todo add working with arrays
        onWorkingTimesChange?.([
          {
            daysPeriods: params.newDaysPeriods,
            period: params.period,
          },
        ]);
      } else if (typeOfRule?.type === TypeOfRuleType.DaysOff) {
        if (daysOffReason !== '') {
          const newDaysOffExcludedThisReason = daysOff.filter(
            (el: any) => el.reason !== daysOffReason
          );
          const newDaysOff = [
            ...newDaysOffExcludedThisReason,
            ...params.newDaysOff,
          ];

          onDaysOffChange?.(newDaysOff);
        } else {
          const newDaysOff = [...params.newDaysOff];
          onDaysOffChange?.(newDaysOff);
        }
      } else {
        const oldFreeDays = holidays?.filter(
          (holiday: any) => !holiday.workHours
        );
        const oldShortenedDays = holidays?.filter(
          (holiday: any) => holiday.workHours
        );

        if (typeOfHolidayId === '0') {
          onHolidaysChange?.([...oldShortenedDays, ...params.newHolidays]);
        } else {
          onHolidaysChange?.([...oldFreeDays, ...params.newHolidays]);
        }
      }

      setIsDialogOpen(false);
    };

    const handleAction = (action: ScheduleMenuActionType, data: any) => {
      return () => {
        switch (action) {
          case ScheduleMenuActionType.WorkTime:
            setTypeOfRuleId(typeOfRuleArr[TypeOfRuleType.WorkingTime]?.id);
            setWorkingTimesIndex(data);
            break;
          case ScheduleMenuActionType.Holidays:
            setTypeOfRuleId(typeOfRuleArr[TypeOfRuleType.Holiday]?.id);
            setTypeOfHolidayId(data);
            break;
          case ScheduleMenuActionType.DaysOff:
            setTypeOfRuleId(typeOfRuleArr[TypeOfRuleType.DaysOff]?.id);
            setDaysOffReason(data);
            break;
          default:
            throw new Error('Not proper ScheduleMenuActionType');
        }
        setIsEdit(false);
        setAnchorEl(null);
        setIsDialogOpen(true);
      };
    };

    useImperativeHandle(ref, () => ({
      showModal: (action: ScheduleMenuActionType, data: any) => {
        handleAction(action, data)();
        setIsEdit(true);
      },
    }));

    return (
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          width: '100%',
        }}
      >
        <Popup
          periodId={periodId}
          onChangePeriodId={onChangePeriodId}
          isOpen={isDialogOpen}
          isEdit={isEdit}
          typeId={typeOfRuleId}
          workingTimes={workingTimes}
          workingTimesIndex={workingTimesIndex}
          daysOff={daysOff}
          daysOffReason={daysOffReason}
          holidays={holidays}
          typeOfHolidayId={typeOfHolidayId}
          period={period}
          onChangeTypeId={(newValue) => setTypeOfRuleId(newValue)}
          onChangeTypeOfHolidayId={(newValue) => setTypeOfHolidayId(newValue)}
          onClose={() => setIsDialogOpen(false)}
          onSubmit={handleSubmit}
        />
        <ActionButton
          fullWidth
          id="action-button"
          aria-controls={open ? 'action-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          endIcon={<PlusIcon />}
          title="Create new rule"
          disableRipple
        />
        <ActionMenu
          id="action-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'action-button',
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <MenuItem
            onClick={handleAction(ScheduleMenuActionType.WorkTime, 0)}
            disableRipple
          >
            <WorkingTimeMenuIcon />
            Working Time
          </MenuItem>
          {workingTimes.length > 0 &&
            (<MenuItem
              onClick={handleAction(ScheduleMenuActionType.Holidays, typeOfHolidayArr[TypeOfHolidayType.FreeDay]?.id)}
              disableRipple
            >
              <HolidaysMenuIcon />
            Holidays
            </MenuItem>)}
          {workingTimes.length > 0 && (
            <MenuItem
              onClick={handleAction(ScheduleMenuActionType.DaysOff, '')}
              disableRipple
            >
              <DaysOffMenuIcon />
              Days off
            </MenuItem>
          )}
        </ActionMenu>
      </Box>
    );
  }
);
